import { useState, useRef, useEffect } from "react";
import PrizePicker from "../PrizePicker.jsx";
import PlushImg from "../PlushImg.jsx";
import { getType, getColor, SIZES, plushFilter, saveTrophy } from "../prizes.js";
import Model3D from "../Model3D.jsx";
import { t } from "../i18n.js";
import { ensureCtx, out } from "../audio.js";

/* ==================== SLÅ MULDVARPEN – whack-a-mole ==========================
   Classic arcade: 9 holes, 60 seconds, moles pop up faster and faster —
   whack them before they duck! Golden moles are worth 3. Prize ladder by
   hits, gold trophy for a truly wild round.
================================================================================ */

const W = 360, H = 540;
const TIME = 60;
const HOLES = [];
for (let r = 0; r < 3; r++)
  for (let c = 0; c < 3; c++)
    HOLES.push({ x: 70 + c * 110, y: 168 + r * 128 });
const WHACK_R = 40;
const GOLD_CHANCE = 0.10;
/* prize ladder — perfect-reflex bot scores ~108, so the trophy sits at ~65% */
const PRIZE = { lille: 20, mellem: 35, stor: 50, pokal: 70 };

/* ---- tiny synth sounds ---- */
function makeAudio() {
  let ctx = null;
  const ensure = () => (ctx = ensureCtx());
  const tone = (f, t0, dur, type = "sine", vol = 0.12) => {
    const c = ensure(); if (!c) return;
    const o = c.createOscillator(), g = c.createGain();
    o.type = type; o.frequency.setValueAtTime(f, t0);
    g.gain.setValueAtTime(vol, t0);
    g.gain.exponentialRampToValueAtTime(0.001, t0 + dur);
    o.connect(g); g.connect(out()); o.start(t0); o.stop(t0 + dur);
  };
  return {
    unlock: ensure,
    bonk() { const c = ensure(); if (!c) return; tone(120, c.currentTime, 0.1, "triangle", 0.16); tone(600, c.currentTime + 0.03, 0.08, "square", 0.06); },
    gold() { const c = ensure(); if (!c) return; [880, 1175].forEach((f, i) => tone(f, c.currentTime + i * 0.07, 0.15, "triangle", 0.12)); },
    whiff() { const c = ensure(); if (c) tone(220, c.currentTime, 0.08, "sawtooth", 0.04); },
    tick() { const c = ensure(); if (c) tone(950, c.currentTime, 0.05, "square", 0.06); },
    end() { const c = ensure(); if (c) tone(320, c.currentTime, 0.6, "square", 0.12); },
    win() { const c = ensure(); if (!c) return; [523, 659, 784, 1046].forEach((f, i) => tone(f, c.currentTime + i * 0.14, 0.3, "triangle", 0.14)); },
  };
}

const prizeFor = (hits) =>
  hits >= PRIZE.pokal ? "pokal" : hits >= PRIZE.stor ? "stor" : hits >= PRIZE.mellem ? "mellem" : hits >= PRIZE.lille ? "lille" : null;

export default function Mole({ onExit }) {
  const [phase, setPhase] = useState("setup");   // setup | playing | finished | failed
  const [hud, setHud] = useState({ hits: 0, left: TIME });
  const [finalHits, setFinalHits] = useState(0);
  const [claimed, setClaimed] = useState(null);

  const canvasRef = useRef(null);
  const wrapRef = useRef(null);
  const audioRef = useRef(null);
  if (!audioRef.current) audioRef.current = makeAudio();

  const startGame = () => {
    setClaimed(null);
    setFinalHits(0);
    setHud({ hits: 0, left: TIME });
    setPhase("playing");
    audioRef.current.unlock();
  };

  /* ================= game loop ================= */
  useEffect(() => {
    if (phase !== "playing") return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const audio = audioRef.current;

    const g = {
      t: 0, hits: 0, over: false, spawnCount: 0,
      moles: HOLES.map(() => ({ state: "down", t: 0, gold: false, peak: 1, riseT: 0.16, sinkT: 0.2 })),
      spawnT: 0.8,
      hammer: null,          // {x, y, t}
      popups: [], lastTick: -1,
    };
    if (localStorage.getItem("carnival.debug")) window.__mo = g;

    /* ---- sizing (house pattern) ---- */
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const fit = () => {
      const wrap = wrapRef.current;
      if (!wrap) return;
      const maxW = Math.min((wrap.parentElement ? wrap.parentElement.clientWidth : W) * 0.96, 440);
      const availH = Math.max(240, window.innerHeight - wrap.getBoundingClientRect().top - 14);
      const scale = Math.min(maxW / W, availH / H);
      canvas.style.width = W * scale + "px";
      canvas.style.height = H * scale + "px";
      canvas.width = W * dpr * scale;
      canvas.height = H * dpr * scale;
      ctx.setTransform(dpr * scale, 0, 0, dpr * scale, 0, 0);
    };
    fit();
    window.addEventListener("resize", fit);
    const ro = new ResizeObserver(fit);
    ro.observe(document.body);

    /* ---- input: instant whack on press (tap game -> touch-action:pan-y) ---- */
    let lastTouch = 0;
    const whackAt = (cx, cy) => {
      if (g.over) return;
      audio.unlock();
      g.hammer = { x: cx, y: cy, t: 0 };
      let hit = false;
      HOLES.forEach((h, i) => {
        const m = g.moles[i];
        if ((m.state === "up" || m.state === "rising") &&
            Math.hypot(cx - h.x, cy - (h.y + 6 - 30 * m.peak)) < WHACK_R) {
          m.state = "hit"; m.t = 0;
          const pts = m.gold ? 3 : 1;
          g.hits += pts;
          hit = true;
          g.popups.push({ x: h.x, y: h.y - 52, txt: "+" + pts, c: m.gold ? "#ffd75e" : "#fff", t: 0 });
          if (m.gold) audio.gold(); else audio.bonk();
        }
      });
      if (!hit) audio.whiff();
    };
    const canvasPos = (clientX, clientY) => {
      const r = canvas.getBoundingClientRect();
      return { x: (clientX - r.left) * (W / r.width), y: (clientY - r.top) * (H / r.height) };
    };
    const onTouch = (e) => {
      lastTouch = Date.now();
      const t0 = e.touches[0];
      const p = canvasPos(t0.clientX, t0.clientY);
      whackAt(p.x, p.y);
    };
    const onMouse = (e) => {
      if (Date.now() - lastTouch < 600) return;   // ghost click after touch
      const p = canvasPos(e.clientX, e.clientY);
      whackAt(p.x, p.y);
    };
    canvas.addEventListener("touchstart", onTouch, { passive: true });
    canvas.addEventListener("mousedown", onMouse);

    /* ---- game logic ----
       difficulty ramps with time AND shifts gear when the score passes
       20 and 50 — good players get punished immediately */
    const difficulty = () => {
      let d = Math.min(1, g.t / TIME) * 0.55;
      if (g.hits >= 20) d += 0.28;
      if (g.hits >= 50) d += 0.34;
      return Math.min(1.2, d);
    };
    const step = (dt) => {
      if (g.over) return;
      g.t += dt;
      const left = Math.max(0, TIME - g.t);

      // ticking final five seconds
      const sec = Math.ceil(left);
      if (sec <= 5 && sec !== g.lastTick && sec > 0) { g.lastTick = sec; audio.tick(); }

      if (left <= 0) {
        g.over = true;
        audio.end();
        const hits = g.hits;
        setTimeout(() => {
          setFinalHits(hits);
          const p = prizeFor(hits);
          if (p === "pokal") { audio.win(); setClaimed(saveTrophy({ game: "Slå muldvarpen" })); setPhase("finished"); }
          else if (p) { audio.win(); setPhase("finished"); }
          else setPhase("failed");
        }, 700);
        return;
      }

      // spawner: faster over time/score, occasionally two at once;
      // every 10th mole is a PEEKER: barely pops up (25/50 %) and ducks fast
      g.spawnT -= dt;
      if (g.spawnT <= 0) {
        const d = difficulty();
        g.spawnT = Math.max(0.32, 1.0 - 0.52 * d + Math.random() * 0.15);
        const downs = g.moles.map((m, i) => (m.state === "down" ? i : -1)).filter((i) => i >= 0);
        const n = Math.random() < 0.2 + 0.4 * d ? 2 : 1;
        for (let k = 0; k < n && downs.length; k++) {
          const idx = downs.splice(Math.floor(Math.random() * downs.length), 1)[0];
          const m = g.moles[idx];
          g.spawnCount += 1;
          const peeker = g.spawnCount % 10 === 0;
          m.state = "rising"; m.t = 0;
          m.gold = Math.random() < GOLD_CHANCE;
          m.peak = peeker ? (Math.random() < 0.5 ? 0.25 : 0.5) : 1;
          m.riseT = peeker ? 0.1 : 0.16;
          m.sinkT = peeker ? 0.11 : 0.2;
          m.upFor = peeker
            ? 0.36 + Math.random() * 0.18
            : 1.15 - 0.5 * d + Math.random() * 0.2;
        }
      }

      for (const m of g.moles) {
        m.t += dt;
        if (m.state === "rising" && m.t > m.riseT) { m.state = "up"; m.t = 0; }
        else if (m.state === "up" && m.t > m.upFor) { m.state = "sinking"; m.t = 0; }
        else if (m.state === "sinking" && m.t > m.sinkT) { m.state = "down"; m.t = 0; }
        else if (m.state === "hit" && m.t > 0.35) { m.state = "down"; m.t = 0; }
      }

      if (g.hammer) { g.hammer.t += dt; if (g.hammer.t > 0.22) g.hammer = null; }
      g.popups.forEach((p) => (p.t += dt));
      g.popups = g.popups.filter((p) => p.t < 0.9);

      if (g.hits !== hudRef.current.hits || sec !== hudRef.current.left) {
        hudRef.current = { hits: g.hits, left: sec };
        setHud(hudRef.current);
      }
    };

    /* ---- drawing ---- */
    const drawMole = (h, m) => {
      const rise = m.state === "rising" ? m.t / m.riseT
        : m.state === "up" ? 1
        : m.state === "sinking" ? 1 - m.t / m.sinkT
        : m.state === "hit" ? Math.max(0, 1 - m.t / 0.18)
        : 0;
      if (rise <= 0) return;
      const my = h.y + 6 - 46 * rise * m.peak;
      ctx.save();
      ctx.beginPath();
      ctx.rect(h.x - 34, h.y - 64, 68, 66);   // only the part above the hole shows
      ctx.clip();
      // body
      ctx.fillStyle = m.gold ? "#e8b83a" : "#6b4a36";
      ctx.beginPath();
      ctx.ellipse(h.x, my + 14, 24, 30, 0, 0, Math.PI * 2);
      ctx.fill();
      // face
      ctx.fillStyle = m.gold ? "#f7d97c" : "#8a6650";
      ctx.beginPath(); ctx.ellipse(h.x, my + 18, 15, 14, 0, 0, Math.PI * 2); ctx.fill();
      // nose + eyes
      ctx.fillStyle = "#e37a9b";
      ctx.beginPath(); ctx.arc(h.x, my + 16, 4.5, 0, Math.PI * 2); ctx.fill();
      ctx.fillStyle = "#1c1c1c";
      if (m.state === "hit") {
        ctx.font = "bold 9px sans-serif"; ctx.textAlign = "center";
        ctx.fillText("✕", h.x - 8, my + 11); ctx.fillText("✕", h.x + 8, my + 11);
      } else {
        ctx.beginPath(); ctx.arc(h.x - 8, my + 8, 2.6, 0, Math.PI * 2); ctx.arc(h.x + 8, my + 8, 2.6, 0, Math.PI * 2); ctx.fill();
      }
      // paws
      ctx.fillStyle = "#e3a8bb";
      ctx.beginPath(); ctx.arc(h.x - 17, my + 30, 5, 0, Math.PI * 2); ctx.arc(h.x + 17, my + 30, 5, 0, Math.PI * 2); ctx.fill();
      ctx.restore();
      // stars when hit (tekst-glyffen ★ farves med fillStyle, ingen emoji)
      if (m.state === "hit") {
        ctx.font = "13px sans-serif"; ctx.textAlign = "center";
        ctx.globalAlpha = Math.max(0, 1 - m.t / 0.35);
        ctx.fillStyle = "#ffd75e";
        ctx.fillText("★", h.x - 20, my - 6);
        ctx.fillStyle = "#fff2b8";
        ctx.fillText("★", h.x + 20, my - 12);
        ctx.globalAlpha = 1;
      }
    };

    const draw = () => {
      // frodigt græs: gradient + store bløde lys/skygge-partier + nister
      const grad = ctx.createLinearGradient(0, 0, 0, H);
      grad.addColorStop(0, "#41924e");
      grad.addColorStop(1, "#276532");
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, W, H);
      for (let i = 0; i < 6; i++) {            // mottling (deterministisk)
        const mx = (i * 137) % W, my2 = 70 + ((i * 227) % (H - 120));
        ctx.fillStyle = i % 2 ? "rgba(255,255,240,0.05)" : "rgba(0,40,10,0.07)";
        ctx.beginPath(); ctx.ellipse(mx, my2, 70, 44, i, 0, Math.PI * 2); ctx.fill();
      }
      ctx.strokeStyle = "rgba(255,255,255,0.12)";
      for (let i = 0; i < 40; i++) {
        const gx = (i * 97) % W, gy = 60 + ((i * 173) % (H - 90));
        ctx.beginPath(); ctx.moveTo(gx, gy); ctx.lineTo(gx + 3, gy - 6); ctx.stroke();
      }

      // bellis + firkløver langs kanterne (uden for hullernes felt)
      const daisy = (x, y) => {
        ctx.fillStyle = "#f7f4ea";
        for (let p = 0; p < 5; p++) {
          const a = (p / 5) * Math.PI * 2;
          ctx.beginPath(); ctx.arc(x + Math.cos(a) * 4.4, y + Math.sin(a) * 4.4, 3, 0, Math.PI * 2); ctx.fill();
        }
        ctx.fillStyle = "#f0c33c";
        ctx.beginPath(); ctx.arc(x, y, 2.6, 0, Math.PI * 2); ctx.fill();
      };
      const clover = (x, y) => {
        ctx.fillStyle = "#1e5b28";
        for (const [ox, oy] of [[-3.4, -1.5], [3.4, -1.5], [0, 3.4]]) {
          ctx.beginPath(); ctx.arc(x + ox, y + oy, 3.4, 0, Math.PI * 2); ctx.fill();
        }
      };
      daisy(26, 96); daisy(W - 30, 118); daisy(40, H - 26); daisy(W - 60, H - 20);
      clover(W - 26, 84); clover(20, 250); clover(W - 20, 372); clover(26, H - 60); clover(W - 40, H - 52);

      // messing-tæller m. mekaniske flip-cifre (mockup-elementet) + tidsbjælke
      const digits = String(g.hits).split("");
      const tiles = Math.max(2, digits.length);
      const plateW = 26 * tiles + 22, plateH = 42, px0 = W / 2 - plateW / 2, py0 = 20;
      const brass = ctx.createLinearGradient(0, py0, 0, py0 + plateH);
      brass.addColorStop(0, "#e9cf8a"); brass.addColorStop(0.5, "#c8a557"); brass.addColorStop(1, "#a37f35");
      ctx.fillStyle = brass;
      ctx.beginPath(); ctx.roundRect(px0, py0, plateW, plateH, 8); ctx.fill();
      ctx.strokeStyle = "#6f5720"; ctx.stroke();
      ctx.fillStyle = "#6f5720";                       // nitter
      for (const [ox, oy] of [[7, 7], [plateW - 7, 7], [7, plateH - 7], [plateW - 7, plateH - 7]]) {
        ctx.beginPath(); ctx.arc(px0 + ox, py0 + oy, 1.8, 0, Math.PI * 2); ctx.fill();
      }
      const pad = String(g.hits).padStart(2, "0").split("");
      pad.forEach((d, i) => {
        const tx = W / 2 - (pad.length * 26) / 2 + i * 26 + 2, ty = py0 + 7;
        const tg = ctx.createLinearGradient(0, ty, 0, ty + 28);
        tg.addColorStop(0, "#3a322d"); tg.addColorStop(0.5, "#241f1c"); tg.addColorStop(1, "#3a322d");
        ctx.fillStyle = tg;
        ctx.beginPath(); ctx.roundRect(tx, ty, 22, 28, 4); ctx.fill();
        ctx.strokeStyle = "rgba(0,0,0,0.5)";
        ctx.beginPath(); ctx.moveTo(tx, ty + 14); ctx.lineTo(tx + 22, ty + 14); ctx.stroke();
        ctx.font = "800 19px 'Nunito', system-ui, sans-serif";
        ctx.textAlign = "center"; ctx.textBaseline = "middle";
        ctx.fillStyle = "#f6eed9";
        ctx.fillText(d, tx + 11, ty + 15);
        ctx.textBaseline = "alphabetic";
      });
      const frac = Math.max(0, 1 - g.t / TIME);
      ctx.fillStyle = "rgba(10,14,20,0.4)";
      ctx.beginPath(); ctx.roundRect(30, 70, W - 60, 12, 6); ctx.fill();
      ctx.strokeStyle = "rgba(233,207,138,0.6)"; ctx.stroke();
      ctx.fillStyle = frac > 0.25 ? "#f5c94e" : "#e05a4e";
      if (frac > 0) { ctx.beginPath(); ctx.roundRect(32, 72, (W - 64) * frac, 8, 4); ctx.fill(); }

      // realistiske huller: ydre muldring, dyb gradient, rodtrævler i kanten
      for (let i = 0; i < HOLES.length; i++) {
        const h = HOLES[i];
        ctx.fillStyle = "rgba(88,58,30,0.35)";         // opgravet muld omkring
        ctx.beginPath(); ctx.ellipse(h.x, h.y + 2, 41, 20, 0, 0, Math.PI * 2); ctx.fill();
        const hg = ctx.createRadialGradient(h.x, h.y, 4, h.x, h.y, 34);
        hg.addColorStop(0, "#150b04");
        hg.addColorStop(0.75, "#241509");
        hg.addColorStop(1, "#3a2413");
        ctx.fillStyle = hg;
        ctx.beginPath(); ctx.ellipse(h.x, h.y, 34, 16, 0, 0, Math.PI * 2); ctx.fill();
        ctx.strokeStyle = "rgba(0,0,0,0.45)";          // skygge i overkanten
        ctx.lineWidth = 3;
        ctx.beginPath(); ctx.ellipse(h.x, h.y - 1, 31, 13, 0, Math.PI * 1.1, Math.PI * 1.9); ctx.stroke();
        ctx.lineWidth = 1;
        ctx.strokeStyle = "rgba(122,86,44,0.8)";       // rodtrævler
        for (let r = 0; r < 7; r++) {
          const a = (r / 7) * Math.PI * 2 + i;
          const rx = h.x + Math.cos(a) * 35, ry = h.y + Math.sin(a) * 16.5;
          ctx.beginPath();
          ctx.moveTo(rx, ry);
          ctx.lineTo(rx + Math.cos(a) * (4 + (r % 3) * 2), ry + Math.sin(a) * (2 + (r % 2) * 2));
          ctx.stroke();
        }
        drawMole(h, g.moles[i]);
        ctx.fillStyle = "#3d2412";                     // forreste læbe
        ctx.beginPath(); ctx.ellipse(h.x, h.y + 5, 36, 14, 0, 0, Math.PI); ctx.fill();
        ctx.fillStyle = "rgba(255,255,255,0.07)";      // lys på læben
        ctx.beginPath(); ctx.ellipse(h.x, h.y + 9, 30, 7, 0, 0, Math.PI); ctx.fill();
      }

      // hammer swing at last tap
      if (g.hammer) {
        const k = g.hammer.t / 0.22;
        ctx.save();
        ctx.translate(g.hammer.x + 16, g.hammer.y - 14);
        ctx.rotate(-0.9 + k * 1.1);
        ctx.fillStyle = "#a06a3a";
        ctx.fillRect(-3, -2, 6, 30);
        ctx.fillStyle = "#5a5f66";
        ctx.beginPath(); ctx.roundRect(-14, -16, 28, 16, 4); ctx.fill();
        ctx.restore();
      }

      // faldende blade: rene funktioner af tiden (ingen ekstra state)
      const LEAF_C = ["#c9a06a", "#b8865a", "#9db06a", "#c77f5f"];
      for (let i = 0; i < 7; i++) {
        const spd = 26 + (i % 3) * 9;
        const ly = ((i * 83 + g.t * spd) % (H + 40)) - 20;
        const lx = ((i * 149) % (W - 40)) + 20 + Math.sin(g.t * 0.7 + i * 1.7) * 16;
        ctx.save();
        ctx.translate(lx, ly);
        ctx.rotate(g.t * 0.8 + i * 2.1 + Math.sin(g.t + i));
        ctx.globalAlpha = 0.75;
        ctx.fillStyle = LEAF_C[i % LEAF_C.length];
        ctx.beginPath(); ctx.ellipse(0, 0, 6.5, 3, 0, 0, Math.PI * 2); ctx.fill();
        ctx.strokeStyle = "rgba(0,0,0,0.18)";
        ctx.beginPath(); ctx.moveTo(-6, 0); ctx.lineTo(6, 0); ctx.stroke();
        ctx.restore();
      }
      ctx.globalAlpha = 1;

      // popups
      for (const p of g.popups) {
        ctx.globalAlpha = Math.max(0, 1 - p.t / 0.9);
        ctx.font = "bold 22px 'Alfa Slab One', Georgia, serif";
        ctx.textAlign = "center";
        ctx.lineWidth = 4; ctx.strokeStyle = "rgba(0,0,0,0.5)";
        ctx.strokeText(p.txt, p.x, p.y - p.t * 26);
        ctx.fillStyle = p.c;
        ctx.fillText(p.txt, p.x, p.y - p.t * 26);
        ctx.globalAlpha = 1;
      }

      // vignette + mahogni-ramme m. messinghjørner (mockup-looket)
      const vg = ctx.createRadialGradient(W / 2, H / 2, 190, W / 2, H / 2, 400);
      vg.addColorStop(0, "rgba(0,0,0,0)");
      vg.addColorStop(1, "rgba(0,0,0,0.22)");
      ctx.fillStyle = vg;
      ctx.fillRect(0, 0, W, H);
      const wood = ctx.createLinearGradient(0, 0, W, H);
      wood.addColorStop(0, "#7a4526"); wood.addColorStop(0.5, "#5d3119"); wood.addColorStop(1, "#7a4526");
      ctx.strokeStyle = wood; ctx.lineWidth = 12;
      ctx.strokeRect(6, 6, W - 12, H - 12);
      ctx.strokeStyle = "rgba(255,220,160,0.25)"; ctx.lineWidth = 1.5;
      ctx.strokeRect(12.5, 12.5, W - 25, H - 25);
      ctx.lineWidth = 1;
      ctx.fillStyle = "#c8a557";                       // messinghjørner
      for (const [cx0, cy0] of [[6, 6], [W - 18, 6], [6, H - 18], [W - 18, H - 18]]) {
        ctx.beginPath(); ctx.roundRect(cx0, cy0, 12, 12, 3); ctx.fill();
        ctx.strokeStyle = "#6f5720"; ctx.strokeRect(cx0 + 0.5, cy0 + 0.5, 11, 11);
      }
    };

    let raf, last = performance.now();
    const loop = (tt) => {
      const dt = Math.min((tt - last) / 1000, 0.033);
      last = tt;
      step(dt);
      draw();
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);

    return () => {
      cancelAnimationFrame(raf);
      delete window.__mo;
      window.removeEventListener("resize", fit);
      ro.disconnect();
      canvas.removeEventListener("touchstart", onTouch);
      canvas.removeEventListener("mousedown", onMouse);
    };
  }, [phase]);
  const hudRef = useRef({ hits: 0, left: TIME });

  /* ================= UI ================= */
  const prize = prizeFor(finalHits);

  return (
    <div className={"mo-root" + (phase !== "setup" ? " ingame" : "")}>
      <style>{css}</style>

      <header className="booth">
        <button className="back" onClick={onExit}>{t("back")}</button>
        <div className="bunting">
          {Array.from({ length: 14 }).map((_, i) => <span key={i} className={i % 2 ? "fl fl-w" : "fl"} />)}
        </div>
        <h1>{t("tile_mole").toUpperCase()}</h1>
        <div className="signs">
          <span className="sign cream">{t("sign_free")}</span>
          <span className="sign red">{t("mo_sign2", { s: TIME })}</span>
        </div>
      </header>

      {phase === "setup" && (
        <main className="panel">
          <p className="lede">{t("mo_lede", { s: TIME })}</p>
          <ul className="rules">
            <li>{t("mo_rule1")}</li>
            <li>{t("mo_rule2")}</li>
            <li>{t("mo_rule3")}</li>
          </ul>
          <p className="prizenote">{t("mo_prizenote", PRIZE)}</p>
          <button className="cta" onClick={startGame}>{t("mo_start")}</button>
        </main>
      )}

      {phase !== "setup" && (
        <>
          <div className="scorebar">
            <span>{t("mo_hits", { n: hud.hits })}</span>
            <span className="last">{hud.left}s</span>
          </div>
          <div ref={wrapRef} className="boardwrap">
            <canvas ref={canvasRef} />
          </div>
        </>
      )}

      {phase === "finished" && prize !== "pokal" && !claimed && (
        <PrizePicker size={prize} game="Slå muldvarpen" onDone={(p) => setClaimed(p)} />
      )}

      {phase === "finished" && claimed && claimed.kind === "trophy" && (
        <div className="overlay">
          <div className="card">
            <div className="ribbon gold">{t("mo_trophy_ribbon")}</div>
            <p className="trophybig"><Model3D src="/models/trophy.glb?v=2" thumb="/images/thumbs/trophy.png" fallback="🏆" size={130} /></p>
            <p className="prizeline">{t("mo_trophyline", { n: finalHits })}</p>
            <button className="cta" onClick={startGame}>{t("play_again")}</button>
            <button className="cta alt" onClick={onExit}>{t("to_menu")}</button>
          </div>
        </div>
      )}

      {phase === "finished" && claimed && claimed.kind !== "trophy" && (
        <div className="overlay">
          <div className="card">
            <div className="ribbon">{t("mo_win")}</div>
            <p className="winline">{t("mo_winline", { n: finalHits })}</p>
            <p className="prizeline">
              <PlushImg className="prizeemoji" type={claimed.type} color={claimed.color} px={SIZES[claimed.size].pick} />
              <br />
              {t("your_prize")} <b>{t("color_" + claimed.color).toLowerCase()} {t("type_" + claimed.type).toLowerCase()}</b> ({t("size_" + claimed.size).toLowerCase()}) {t("in_room")}
            </p>
            <button className="cta" onClick={startGame}>{t("play_again")}</button>
            <button className="cta alt" onClick={onExit}>{t("to_menu")}</button>
          </div>
        </div>
      )}

      {phase === "failed" && (
        <div className="overlay">
          <div className="card">
            <div className="ribbon dark">{t("mo_fail")}</div>
            <p className="winline">{t("mo_failline", { n: finalHits, need: PRIZE.lille })}</p>
            <button className="cta" onClick={startGame}>{t("try_again")}</button>
            <button className="cta alt" onClick={onExit}>{t("to_menu")}</button>
          </div>
        </div>
      )}
    </div>
  );
}

const css = `
.mo-root{
  --red:#C8352E; --cream:#F6EED9; --ink:#22301c;
  min-height:100vh; background:#1e3d16;
  background-image:radial-gradient(circle at 50% -10%, #3c6b2c 0%, #1e3d16 60%);
  font-family:'Nunito',system-ui,sans-serif; color:var(--cream);
  display:flex; flex-direction:column; align-items:center; padding-bottom:24px;
  -webkit-user-select:none; user-select:none; touch-action:manipulation;
}
.booth{width:100%; max-width:520px; text-align:center; padding:10px 12px 6px; position:relative;}
.back{
  position:absolute; left:10px; top:12px; z-index:2;
  background:var(--cream); color:var(--ink); border:none; border-radius:8px;
  font-family:inherit; font-weight:800; font-size:13px; padding:7px 11px; cursor:pointer;
  box-shadow:0 3px 0 #b3a880;
}
.bunting{display:flex; justify-content:center; gap:2px; margin-bottom:4px;}
.fl{width:0;height:0;border-left:9px solid transparent;border-right:9px solid transparent;border-top:14px solid var(--red);}
.fl-w{border-top-color:var(--cream);}
.booth h1{
  font-family:'Alfa Slab One',Georgia,serif; font-size:clamp(22px,6.4vw,38px);
  margin:0; letter-spacing:2px; color:var(--cream);
  text-shadow:0 3px 0 #7e1f1a, 0 6px 12px rgba(0,0,0,.45);
}
.signs{display:flex; gap:8px; justify-content:center; margin-top:6px; flex-wrap:wrap;}
.sign{font-weight:800; font-size:12px; padding:4px 10px; border-radius:3px; letter-spacing:.5px;}
.sign.cream{background:var(--cream); color:var(--ink); border:1px solid #c9bf9f;}
.sign.red{background:var(--red); color:#fff;}
.ingame .bunting{display:none;}
.ingame .signs{display:none;}
.ingame .booth{padding:8px 12px 2px;}
.ingame .booth h1{font-size:clamp(18px,5vw,26px); text-shadow:0 2px 0 #7e1f1a, 0 4px 8px rgba(0,0,0,.45);}
.ingame .back{top:6px; padding:5px 9px;}
.panel{
  width:min(92%,440px); background:var(--cream); color:var(--ink);
  border-radius:12px; padding:18px 20px 22px; margin-top:14px;
  box-shadow:0 10px 30px rgba(0,0,0,.4);
}
.lede{margin:0 0 12px; font-size:15px; line-height:1.45;}
.rules{list-style:none; margin:0; padding:0;}
.rules li{font-weight:800; font-size:14px; padding:6px 0; border-bottom:1px dashed #cbbf9c;}
.prizenote{margin:12px 0 0; font-size:11.5px; font-weight:800; opacity:.75; line-height:1.4; white-space:nowrap; overflow:hidden; text-overflow:ellipsis;}
.cta{
  width:100%; margin-top:20px; padding:14px; font-size:19px; font-weight:800; cursor:pointer;
  font-family:'Alfa Slab One',Georgia,serif; letter-spacing:1px;
  background:var(--red); color:#fff; border:none; border-radius:10px;
  box-shadow:0 4px 0 #7e1f1a;
}
.cta:active{transform:translateY(3px); box-shadow:0 1px 0 #7e1f1a;}
.cta.alt{margin-top:10px; background:#3c6b2c; box-shadow:0 4px 0 #24421a; font-size:15px; padding:11px;}
.cta.alt:active{box-shadow:0 1px 0 #24421a;}
.scorebar{
  width:min(96%,520px); display:flex; justify-content:space-between; align-items:center;
  gap:8px; font-size:14px; font-weight:800; padding:6px 4px 4px;
}
.last{background:rgba(255,255,255,.15); padding:2px 10px; border-radius:20px;}
.boardwrap{
  width:auto; max-width:96%; border-radius:10px; overflow:hidden;
  border:4px solid #6b4a36; box-shadow:0 12px 30px rgba(0,0,0,.45);
}
.boardwrap canvas{display:block; touch-action:pan-y;}
.overlay{
  position:fixed; inset:0; background:rgba(8,18,6,.75); z-index:10;
  display:flex; align-items:center; justify-content:center;
}
.card{
  width:min(88%,380px); background:var(--cream); color:var(--ink);
  border-radius:14px; padding:0 22px 22px; text-align:center;
  box-shadow:0 18px 50px rgba(0,0,0,.6);
}
.ribbon{
  font-family:'Alfa Slab One',Georgia,serif; font-size:22px; letter-spacing:1px;
  background:var(--red); color:#fff; margin:0 -22px 14px; padding:14px;
  border-radius:14px 14px 0 0;
}
.ribbon.gold{background:linear-gradient(180deg,#f5c94e,#c8951d); color:#4a3308;}
.ribbon.dark{background:#24421a;}
.winline{font-size:16px; font-weight:800; margin:4px 0 12px;}
.trophybig{margin:6px 0 2px; display:flex; justify-content:center;}
.prizeline{font-size:14px; font-weight:800; margin:0 0 12px; line-height:1.5;}
.prizeemoji{line-height:1.2; display:inline-block;}
`;
